import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useElementCommitments } from '@/features/commitments';
import { useOmnibox } from '@/features/search';
import { Field } from '@/shared/ui';

import styles from './Campaigns.module.css';

/** Wybrane źródło — tyle, ile trzeba do wysłania żądania i pokazania zdania. */
export interface PickedSurtSource {
  commitmentId: number;
  title: string;
  elementTitle: string;
}

/**
 * Wskazanie źródła surta (decyzja 90): architekt wybiera **konkretne zobowiązanie na konkretnym
 * elemencie**, a nie „migrację" jako pojęcie. Dwa kroki: element z szukania, potem jedno z jego
 * otwartych zobowiązań.
 *
 * ⭐ Szukanie jedzie **tym samym endpointem co omnibox** — druga reguła dopasowania rozjechałaby
 * się z pierwszą przy najbliższej poprawce (ten sam argument, którym omnibox rozwiązuje nazwy).
 *
 * ⚠ Lista pokazuje wyłącznie **otwarte** zobowiązania: źródło ma się dopiero domknąć. Zrobione
 * obudziłoby wsad w chwili uruchomienia — od tego jest tryb „teraz", nie surt na wykonanym.
 */
export function SurtSourcePicker({
  value,
  onChange,
}: {
  value: PickedSurtSource | null;
  onChange: (value: PickedSurtSource | null) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [element, setElement] = useState<{ id: number; title: string } | null>(null);

  const search = useOmnibox(query, value === null && element === null && query.trim() !== '');
  const commitments = useElementCommitments(element?.id ?? null, 'open', value === null);

  function reset() {
    onChange(null);
    setElement(null);
    setQuery('');
  }

  if (value !== null) {
    return (
      <p className={styles['pickedSource']}>
        {t('campaigns.create.surtSource.picked', {
          title: value.title,
          element: value.elementTitle,
        })}
        <button type="button" className={styles['inlineAction']} onClick={reset}>
          {t('campaigns.create.surtSource.change')}
        </button>
      </p>
    );
  }

  if (element !== null) {
    const open = commitments.data ?? [];

    return (
      <div className={styles['sourcePicker']}>
        <p className={styles['muted']}>
          {t('campaigns.create.surtSource.pickCommitment', { element: element.title })}
          <button type="button" className={styles['inlineAction']} onClick={() => setElement(null)}>
            {t('campaigns.create.surtSource.change')}
          </button>
        </p>

        {commitments.isPending ? null : open.length === 0 ? (
          <p className={styles['muted']}>{t('campaigns.create.surtSource.noOpen')}</p>
        ) : (
          <ul className={styles['sourceList']}>
            {open.map((commitment) => (
              <li key={commitment.id}>
                <button
                  type="button"
                  className={styles['sourceOption']}
                  onClick={() =>
                    onChange({
                      commitmentId: commitment.id,
                      title: commitment.title,
                      elementTitle: element.title,
                    })
                  }
                >
                  {commitment.title}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  const hits = search.data?.hits ?? [];

  return (
    <div className={styles['sourcePicker']}>
      <Field
        label={t('campaigns.create.surtSource.searchElement')}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        hint={t('campaigns.create.surtSource.searchHint')}
      />

      {query.trim() !== '' && hits.length > 0 ? (
        <ul className={styles['sourceList']}>
          {hits.slice(0, 7).map((hit) => (
            <li key={hit.elementId}>
              <button
                type="button"
                className={styles['sourceOption']}
                onClick={() => setElement({ id: hit.elementId, title: hit.title })}
              >
                {hit.title}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
